import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Archive, Calendar, ChevronDown, Download, Eye, AlertTriangle, Check, X, Users, History } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function SeasonManager({ onSeasonChange }) {
  const [seasons, setSeasons] = useState([]);
  const [activeYear, setActiveYear] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showPastSeason, setShowPastSeason] = useState(null);
  const [pastSeasonData, setPastSeasonData] = useState(null);
  const [loadingPast, setLoadingPast] = useState(false);
  const [newYear, setNewYear] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [archiving, setArchiving] = useState(false);

  const fetchSeasons = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/seasons`);
      if (res.ok) {
        const data = await res.json();
        setSeasons(data.seasons);
        setActiveYear(data.active_year);
      }
    } catch (e) {
      console.error('Failed to fetch seasons:', e);
    }
  }, []);

  useEffect(() => { fetchSeasons(); }, [fetchSeasons]);

  const handleArchive = async () => {
    const yearNum = parseInt(newYear);
    if (!yearNum || yearNum < 2020 || yearNum > 2100) {
      toast.error('Enter a valid year (2020-2100)');
      return;
    }
    if (confirmText !== `ARCHIVE ${activeYear}`) {
      toast.error(`Type "ARCHIVE ${activeYear}" to confirm`);
      return;
    }

    setArchiving(true);
    try {
      const res = await fetch(`${API_URL}/api/seasons/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_year: yearNum })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setShowArchiveModal(false);
        setNewYear('');
        setConfirmText('');
        fetchSeasons();
        if (onSeasonChange) onSeasonChange(yearNum);
      } else {
        toast.error(data.detail || 'Archive failed');
      }
    } catch (e) {
      toast.error('Error: ' + e.message);
    } finally {
      setArchiving(false);
    }
  };

  const loadPastSeason = async (year) => {
    setLoadingPast(true);
    setShowPastSeason(year);
    try {
      const res = await fetch(`${API_URL}/api/seasons/${year}/data`);
      if (res.ok) {
        const data = await res.json();
        setPastSeasonData(data);
      } else {
        toast.error('Could not load season data');
        setPastSeasonData(null);
      }
    } catch (e) {
      toast.error('Error loading season: ' + e.message);
    } finally {
      setLoadingPast(false);
    }
  };

  const exportSeasonCSV = async (year) => {
    try {
      const res = await fetch(`${API_URL}/api/seasons/${year}/export/csv`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `season_${year}_archive.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('CSV export failed');
    }
  };

  const archivedSeasons = seasons.filter(s => s.status === 'archived');

  return (
    <div className="space-y-4" data-testid="season-manager">
      {/* Current Season */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-600" />
            Active Season: {activeYear}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            {seasons.find(s => s.status === 'active') && (
              <>
                <Badge variant="outline" className="text-xs bg-green-100 text-green-800">Active</Badge>
                <span>{seasons.find(s => s.status === 'active')?.camper_count || 0} campers</span>
                <span>{seasons.find(s => s.status === 'active')?.group_count || 0} groups</span>
              </>
            )}
          </div>
          <Button
            onClick={() => setShowArchiveModal(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
            size="sm"
            data-testid="archive-season-btn"
          >
            <Archive className="w-3 h-3 mr-1" />
            Archive Season & Start New
          </Button>
        </CardContent>
      </Card>

      {/* Archived Seasons */}
      {archivedSeasons.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <History className="w-4 h-4 text-gray-500" />
              Past Seasons ({archivedSeasons.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {archivedSeasons.map(s => (
              <div key={s.year} className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded border text-xs">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs bg-gray-100">{s.year}</Badge>
                  <span className="text-gray-600">{s.camper_count || 0} campers</span>
                  {s.assigned_count && <span className="text-gray-500">({s.assigned_count} assigned)</span>}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => loadPastSeason(s.year)} data-testid={`view-season-${s.year}`}>
                    <Eye className="w-3 h-3 mr-1" /> View
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => exportSeasonCSV(s.year)} data-testid={`export-season-${s.year}`}>
                    <Download className="w-3 h-3 mr-1" /> CSV
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="archive-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-2 mb-4 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              <h3 className="text-lg font-bold">Archive Season {activeYear}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              This will archive all <strong>{activeYear}</strong> grouping data and start a fresh board for the new season.
              Camper group history will be preserved. This action cannot be undone.
            </p>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">New Season Year</label>
                <Input
                  type="number"
                  min="2020"
                  max="2100"
                  value={newYear}
                  onChange={e => setNewYear(e.target.value)}
                  placeholder={`e.g. ${activeYear + 1}`}
                  className="text-sm"
                  data-testid="new-year-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">
                  Type <span className="font-mono bg-gray-100 px-1 rounded">ARCHIVE {activeYear}</span> to confirm
                </label>
                <Input
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder={`ARCHIVE ${activeYear}`}
                  className="text-sm font-mono"
                  data-testid="confirm-archive-input"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" size="sm" onClick={() => { setShowArchiveModal(false); setNewYear(''); setConfirmText(''); }}>
                Cancel
              </Button>
              <Button
                onClick={handleArchive}
                disabled={archiving || confirmText !== `ARCHIVE ${activeYear}` || !newYear}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                size="sm"
                data-testid="confirm-archive-btn"
              >
                {archiving ? 'Archiving...' : `Archive ${activeYear} & Start ${newYear}`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Past Season Viewer Modal */}
      {showPastSeason && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="past-season-modal">
          <div className="bg-white rounded-xl shadow-2xl w-[95vw] h-[90vh] flex flex-col mx-4">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-800 text-white rounded-t-xl shrink-0">
              <div className="flex items-center gap-2">
                <Archive className="w-5 h-5" />
                <h3 className="text-lg font-bold">Season {showPastSeason} — Archived Groups</h3>
                <Badge className="bg-gray-600 text-xs">Read Only</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => exportSeasonCSV(showPastSeason)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 text-xs">
                  <Download className="w-3 h-3 mr-1" /> CSV
                </Button>
                <button onClick={() => { setShowPastSeason(null); setPastSeasonData(null); }} className="p-1 hover:bg-white/20 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-auto p-4">
              {loadingPast ? (
                <div className="flex items-center justify-center h-full text-gray-500">Loading archived data...</div>
              ) : pastSeasonData ? (
                <PastSeasonView data={pastSeasonData} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">No data available</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PastSeasonView({ data }) {
  const { campers, groups, stats } = data;
  const groupMap = {};
  for (const g of groups) {
    groupMap[g.code] = { ...g, campers: [] };
  }
  for (const c of campers) {
    const grp = c.assigned_group;
    if (grp && groupMap[grp]) {
      groupMap[grp].campers.push(c);
    }
  }

  const sortedGroups = Object.values(groupMap).sort((a, b) => a.code.localeCompare(b.code));

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4 text-xs">
        <Badge variant="outline">{stats?.total_campers || 0} campers</Badge>
        <Badge variant="outline">{stats?.assigned_campers || 0} assigned</Badge>
        <Badge variant="outline">{stats?.total_groups || 0} groups</Badge>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {sortedGroups.map(g => (
          <div key={g.code} className="border rounded-lg p-3 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-sm">{g.code} {g.name}</div>
              <Badge variant="outline" className="text-xs">{g.campers.length}/{g.max_size}</Badge>
            </div>
            <div className="text-xs text-gray-500 mb-2">{g.grade} | {g.gender}</div>
            <div className="space-y-0.5">
              {g.campers.sort((a, b) => a.last_name.localeCompare(b.last_name)).map(c => (
                <div key={c.id} className="text-xs py-0.5 px-1 rounded hover:bg-gray-100 flex items-center justify-between">
                  <span>{c.first_name} {c.last_name}</span>
                  <span className="text-gray-400">{c.grade}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SeasonManager;
